/**
 * Plan pricing editor — /saas/plans/$planId
 *
 * Shows all price components for a plan, lets the user:
 *  • Enable tiered billing (if uses_components = false)
 *  • Create / edit / delete price components
 *  • Open the AI tier designer to generate tier structure from usage data
 */
import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";

import { api } from "@/lib/api";
import { saasComponentsQueryOptions, saasPlansQueryOptions, saasKeys } from "@/lib/queries";
import { useToast } from "@/components/Toaster";
import { PriceComponentDialog, type PendingDesign } from "@/components/PriceComponentDialog";
import { AiTierDesignerDialog, type AcceptedTierDesign } from "@/components/AiTierDesignerDialog";
import type { PriceComponentOut, PricingTierOut } from "@/lib/types";

// ---------------------------------------------------------------------------
// Route
// ---------------------------------------------------------------------------

export const Route = createFileRoute("/_auth/saas/plans/$planId")({
  component: PlanPricingPage,
});

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const MODEL_COLORS: Record<string, "default" | "primary" | "secondary" | "info" | "success"> = {
  flat:       "default",
  per_unit:   "info",
  graduated:  "primary",
  volume:     "secondary",
  stairstep:  "success",
};

function TierTable({ tiers, isStairstep }: { tiers: PricingTierOut[]; isStairstep: boolean }) {
  if (!tiers.length) return <Typography variant="caption" color="text.secondary">No tiers</Typography>;
  return (
    <div className="text-xs">
      <div className="grid grid-cols-[80px_80px_100px] gap-2 font-semibold text-slate-400 pb-1">
        <span>Lower</span>
        <span>Upper</span>
        <span>{isStairstep ? "Flat (¢)" : "Rate (¢)"}</span>
      </div>
      {[...tiers].sort((a, b) => a.tier_index - b.tier_index).map((t) => (
        <div key={t.tier_index} className="grid grid-cols-[80px_80px_100px] gap-2 font-mono text-slate-600 border-t border-slate-100 py-0.5">
          <span>{t.lower_bound}</span>
          <span>{t.upper_bound ?? "∞"}</span>
          <span>{isStairstep ? (t.flat_amount_cents ?? "—") : (t.rate_cents ?? "—")}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function PlanPricingPage() {
  const { planId } = Route.useParams();
  const qc = useQueryClient();
  const toast = useToast();

  // Fetch all plans, find this one (no single-plan GET endpoint)
  const { data: plans } = useSuspenseQuery(saasPlansQueryOptions());
  const plan = plans.find((p) => p.id === planId);

  // Fetch components
  const { data: components = [] } = useQuery(saasComponentsQueryOptions(planId));

  // Dialog state
  const [compDialogOpen, setCompDialogOpen]         = useState(false);
  const [editing, setEditing]                       = useState<PriceComponentOut | undefined>();
  const [aiDialogOpen, setAiDialogOpen]             = useState(false);
  const [aiTargetComp, setAiTargetComp]             = useState<PriceComponentOut | undefined>();
  const [pendingDesign, setPendingDesign]            = useState<PendingDesign | null>(null);

  // Migrate to components
  const migrate = useMutation({
    mutationFn: () =>
      api(`/api/saas/plans/${planId}/migrate-to-components`, { method: "POST" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.plans() });
      qc.invalidateQueries({ queryKey: saasKeys.components(planId) });
      toast.success("Tiered billing enabled for this plan");
    },
    onError: (e: Error) => toast.error(`Migration failed: ${e.message}`),
  });

  // Delete component
  const deleteComp = useMutation({
    mutationFn: (compId: string) =>
      api(`/api/saas/plans/${planId}/components/${compId}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: saasKeys.components(planId) });
      toast.success("Component removed");
    },
    onError: (e: Error) => toast.error(`Delete failed: ${e.message}`),
  });

  function openCreate(design?: AcceptedTierDesign) {
    setEditing(undefined);
    setPendingDesign(design ?? null);
    setCompDialogOpen(true);
  }

  function openEdit(comp: PriceComponentOut) {
    setEditing(comp);
    setPendingDesign(null);
    setCompDialogOpen(true);
  }

  function openAiDesigner(comp?: PriceComponentOut) {
    setAiTargetComp(comp);
    setAiDialogOpen(true);
  }

  function handleAiAccept(design: AcceptedTierDesign) {
    // AcceptedTierDesign === PendingDesign (same shape)
    setEditing(aiTargetComp);
    setPendingDesign(design as PendingDesign);
    setCompDialogOpen(true);
  }

  if (!plan) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">Plan not found</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <IconButton component={Link} to="/saas" size="small">
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography variant="h5" fontWeight={600}>{plan.name}</Typography>
        <Chip label={plan.plan_code} size="small" variant="outlined" />
        <Chip
          label={plan.is_active ? "Active" : "Inactive"}
          size="small"
          color={plan.is_active ? "success" : "default"}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, ml: 5 }}>
        Pricing editor
      </Typography>

      {/* Migration banner */}
      {!plan.uses_components && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            <Button
              size="small"
              variant="outlined"
              onClick={() => migrate.mutate()}
              disabled={migrate.isPending}
            >
              {migrate.isPending ? "Enabling…" : "Enable tiered components"}
            </Button>
          }
        >
          This plan uses legacy flat billing. Enable tiered components to configure
          graduated, volume, or stairstep pricing.
        </Alert>
      )}

      {/* Component list */}
      {plan.uses_components && (
        <>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              Price components ({components.length})
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button
                startIcon={<AutoAwesomeIcon />}
                size="small"
                variant="outlined"
                onClick={() => openAiDesigner()}
              >
                AI designer
              </Button>
              <Button
                startIcon={<AddIcon />}
                size="small"
                variant="contained"
                onClick={() => openCreate()}
              >
                Add component
              </Button>
            </Stack>
          </Stack>

          {components.length === 0 && (
            <Alert severity="info">
              No components yet. Add a component to define how this plan is priced.
            </Alert>
          )}

          <Stack spacing={2}>
            {components.map((comp) => {
              const isStairstep = comp.pricing_model === "stairstep";
              const isTiered = ["graduated", "volume", "stairstep"].includes(comp.pricing_model);
              return (
                <Box
                  key={comp.id}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    p: 2,
                    opacity: comp.is_active ? 1 : 0.55,
                  }}
                >
                  {/* Card header */}
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography variant="subtitle2" fontWeight={600}>{comp.name}</Typography>
                      <Chip label={comp.component_code} size="small" variant="outlined" sx={{ fontFamily: "monospace" }} />
                      <Chip
                        label={comp.pricing_model}
                        size="small"
                        color={MODEL_COLORS[comp.pricing_model] ?? "default"}
                      />
                      <Chip
                        label={comp.billing_cadence.replace("_", " ")}
                        size="small"
                        variant="outlined"
                      />
                      {!comp.is_active && <Chip label="Inactive" size="small" color="default" />}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="AI designer">
                        <IconButton size="small" onClick={() => openAiDesigner(comp)}>
                          <AutoAwesomeIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(comp)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            if (confirm(`Delete "${comp.name}"?`)) deleteComp.mutate(comp.id);
                          }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Summary row */}
                  <Stack direction="row" spacing={3} sx={{ mt: 1.5, mb: isTiered ? 1.5 : 0 }}>
                    {comp.pricing_model === "flat" && comp.flat_amount_cents != null && (
                      <Typography variant="body2" color="text.secondary">
                        {comp.currency_code} {(comp.flat_amount_cents / 100).toFixed(2)} / period
                      </Typography>
                    )}
                    {comp.pricing_model === "per_unit" && comp.unit_rate_cents != null && (
                      <Typography variant="body2" color="text.secondary">
                        {comp.currency_code} {(comp.unit_rate_cents / 100).toFixed(4)} / {comp.unit_label ?? "unit"}
                      </Typography>
                    )}
                    {comp.usage_meter && (
                      <Typography variant="body2" color="text.secondary">
                        Meter: <code>{comp.usage_meter}</code>
                      </Typography>
                    )}
                    {comp.minimum_cents > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        Min: {(comp.minimum_cents / 100).toFixed(2)}
                      </Typography>
                    )}
                    {comp.maximum_cents != null && (
                      <Typography variant="body2" color="text.secondary">
                        Max: {(comp.maximum_cents / 100).toFixed(2)}
                      </Typography>
                    )}
                  </Stack>

                  {/* Tier table */}
                  {isTiered && (
                    <>
                      <Divider sx={{ mb: 1 }} />
                      <TierTable tiers={comp.tiers} isStairstep={isStairstep} />
                    </>
                  )}
                </Box>
              );
            })}
          </Stack>
        </>
      )}

      {/* Dialogs */}
      <PriceComponentDialog
        open={compDialogOpen}
        onClose={() => { setCompDialogOpen(false); setPendingDesign(null); }}
        planId={planId}
        component={editing}
        pendingDesign={pendingDesign ?? undefined}
      />

      <AiTierDesignerDialog
        open={aiDialogOpen}
        onClose={() => setAiDialogOpen(false)}
        initialLabel={aiTargetComp?.unit_label ?? ""}
        onAccept={handleAiAccept}
      />
    </Box>
  );
}
